import React, { lazy, Suspense, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";


import Header from "./components/Header";
import Footer from "./components/Footer";
import { trackPageView } from "./utils/analytics";


import ScrollToTop from "./components/ScrollToTop";

// Lazy load components
const HomePage = lazy(() => import("./components/HomePage"));
const DistrictVillagesPage = lazy(() => import("./components/DistrictVillagesPage"));
const VillageDetails = lazy(() => import("./components/VillageDetails"));
const DistrictReligionPage = lazy(() => import("./components/DistrictReligionPage"));
const AboutData = lazy(() => import("./pages/AboutData"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
const HeritagePlacesPage = lazy(() => import("./pages/HeritagePlacesPage"));
const DynamicSlugPage = lazy(() => import("./components/DynamicSlugPage"));
const ReligionDataPage = lazy(() => import("./components/ReligionDataPage"));
const TopReligionStatesPage = lazy(() => import("./components/TopReligionStatesPage"));
const BottomReligionStatesPage = lazy(() => import("./components/BottomReligionStatesPage"));
const TopReligionDistrictsPage = lazy(() => import("./components/TopReligionDistrictsPage"));
const BottomReligionDistrictsPage = lazy(() => import("./components/BottomReligionDistrictsPage"));
const ReligionAnalysisPage = lazy(() => import("./components/ReligionAnalysisPage"));
const IndiaReligionPage = lazy(() => import("./components/IndiaReligionPage"));
const IndiaGenderAnalysisPage = lazy(() => import("./components/IndiaGenderAnalysisPage"));
const IndiaUrbanRuralPage = lazy(() => import("./components/IndiaUrbanRuralPage"));
const DiversityAnalysisPage = lazy(() => import("./components/DiversityAnalysisPage"));
const GenderGapAnalysisPage = lazy(() => import("./components/GenderGapAnalysisPage"));
const LanguageAnalysisPage = lazy(() => import("./components/LanguageAnalysisPage"));
const SubdistrictReligionPage = lazy(() => import("./components/SubdistrictReligionPage"));
const StateReligionPage = lazy(() => import("./components/StateReligionPage"));
const StatePage = lazy(() => import("./components/StatePage"));
const Census2027Page = lazy(() => import("./components/Census2027Page"));
const CensusSelfEnumerationGuidePage = lazy(() => import("./components/CensusSelfEnumerationGuidePage"));
const CensusHousingQuestionsPage = lazy(() => import("./components/CensusHousingQuestionsPage"));
const CensusRequiredDetailsPage = lazy(() => import("./components/CensusRequiredDetailsPage"));
const CensusErrorsFaqPage = lazy(() => import("./components/CensusErrorsFaqPage"));
const TempleDetailPage = lazy(() => import("./components/TempleDetailPage"));
const VillageBanksPage = lazy(() => import("./components/VillageBanksPage"));


const IfscDetailsPage = lazy(() => import("./components/IfscDetailsPage"));
const IfscBankLocationPage = lazy(() => import("./components/IfscBankLocationPage"));
const IfscBanksDirectoryPage = lazy(() => import("./components/IfscBanksDirectoryPage"));
const IfscBankPage = lazy(() => import("./components/IfscBankPage"));
const DataCorrectionPage = lazy(() => import("./pages/DataCorrection"));
const PoliticsHomePage = lazy(() => import("./components/PoliticsHomePage"));
const PoliticsStatePage = lazy(() => import("./components/PoliticsStatePage"));
const PoliticsConstituencyPage = lazy(() => import("./components/PoliticsConstituencyPage"));
const PoliticsCandidatePage = lazy(() => import("./components/PoliticsCandidatePage"));
const GHMCElectionsPage = lazy(() => import("./components/GHMCElectionsPage"));
const GHMCWardDetailsPage = lazy(() => import("./components/GHMCWardDetailsPage"));
const GHMCAnalysisPage = lazy(() => import("./components/GHMCAnalysisPage"));
const GHMCElectionDatesPage = lazy(() => import("./components/GHMCElectionDatesPage"));


const Loading = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

function App() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return (
    <div className="app-container">
      <ScrollToTop />
      <Header />
      <main className="main-content">
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about-us" element={<AboutData />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/data-correction" element={<DataCorrectionPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsConditions />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/sitemap" element={<SitemapPage />} />
            <Route path="/heritage-places" element={<HeritagePlacesPage />} />

            <Route
              path="/religion"
              element={<ReligionDataPage />}
            />

            <Route
              path="/religion/:state"
              element={<StateReligionPage />}
            />

            <Route
              path="/religion/:stateSlug/:districtSlug"
              element={<DistrictReligionPage />}
            />

            <Route
              path="/religion/:stateSlug/:districtSlug/:subdistrictSlug"
              element={<SubdistrictReligionPage />}
            />

            <Route
              path="/rankings/religion/:religion"
              element={<TopReligionStatesPage />}
            />

            <Route
              path="/rankings/minority/:religion"
              element={<BottomReligionStatesPage />}
            />

            <Route
              path="/analysis/:topic"
              element={<ReligionAnalysisPage />}
            />

            <Route
              path="/rankings/district/majority/:religion"
              element={<TopReligionDistrictsPage />}
            />

            <Route
              path="/rankings/district/minority/:religion"
              element={<BottomReligionDistrictsPage />}
            />

            <Route
              path="/india-census-2011"
              element={<IndiaReligionPage />}
            />

            <Route
              path="/analysis/religion-gender-ratio"
              element={<IndiaGenderAnalysisPage />}
            />

            <Route
              path="/analysis/religion-urban-rural"
              element={<IndiaUrbanRuralPage />}
            />

            <Route
              path="/analysis/diversity-index"
              element={<DiversityAnalysisPage />}
            />

            <Route
              path="/analysis/critical-gender-gaps"
              element={<GenderGapAnalysisPage />}
            />

            <Route
              path="/analysis/language-census-2011"
              element={<LanguageAnalysisPage />}
            />

            <Route
              path="/census-2027-upcoming"
              element={<Census2027Page />}
            />
            <Route
              path="/census-2027-upcoming/self-enumeration-guide"
              element={<CensusSelfEnumerationGuidePage />}
            />
            <Route
              path="/census-2027-upcoming/housing-census-questions"
              element={<CensusHousingQuestionsPage />}
            />
            <Route
              path="/census-2027-upcoming/required-details"
              element={<CensusRequiredDetailsPage />}
            />
            <Route
              path="/census-2027-upcoming/errors-faq"
              element={<CensusErrorsFaqPage />}
            />
            <Route
              path="/:langCode/census-2027-upcoming"
              element={<Census2027Page />}
            />
            <Route
              path="/:langCode/census-2027-upcoming/self-enumeration-guide"
              element={<CensusSelfEnumerationGuidePage />}
            />
            <Route
              path="/:langCode/census-2027-upcoming/housing-census-questions"
              element={<CensusHousingQuestionsPage />}
            />
            <Route
              path="/:langCode/census-2027-upcoming/required-details"
              element={<CensusRequiredDetailsPage />}
            />
            <Route
              path="/:langCode/census-2027-upcoming/errors-faq"
              element={<CensusErrorsFaqPage />}
            />


            <Route
              path="/:state/destination/:templeSlug"
              element={<TempleDetailPage />}
            />

            <Route
              path="/:state/:district/:subdistrict/:village"
              element={<VillageDetails />}
            />

            <Route
              path="/:state/:district/:subdistrict/:village/banks"
              element={<VillageBanksPage />}
            />

            <Route path="/ifsc" element={<IfscDetailsPage />} />
            <Route path="/ifsc/banks" element={<IfscBanksDirectoryPage />} />
            <Route path="/ifsc/bank/:bankSlug" element={<IfscBankPage />} />
            <Route path="/ifsc/:bankSlug/:locationSlug" element={<IfscBankLocationPage />} />
            <Route path="/ifsc/:bankSlug/:locationSlug/:localitySlug" element={<IfscBankLocationPage />} />
            <Route path="/ifsc/:bankSlug/:locationSlug/:localitySlug/:branchSlug" element={<IfscBankLocationPage />} />
            <Route path="/ifsc/:ifsc" element={<IfscDetailsPage />} />
            <Route path="/politics/assembly-2026" element={<PoliticsHomePage dataset="assembly-2026" />} />
            <Route path="/politics/assembly-2026/:state" element={<PoliticsStatePage dataset="assembly-2026" />} />
            <Route path="/politics/assembly-2026/:state/:constituency" element={<PoliticsConstituencyPage dataset="assembly-2026" />} />
            <Route path="/politics/assembly-2026/:state/:constituency/:candidate" element={<PoliticsCandidatePage dataset="assembly-2026" />} />
            <Route path="/politics/assembly-2021" element={<PoliticsHomePage dataset="assembly-2021" />} />
            <Route path="/politics/assembly-2021/:state" element={<PoliticsStatePage dataset="assembly-2021" />} />
            <Route path="/politics/assembly-2021/:state/:constituency" element={<PoliticsConstituencyPage dataset="assembly-2021" />} />
            <Route path="/politics/assembly-2021/:state/:constituency/:candidate" element={<PoliticsCandidatePage dataset="assembly-2021" />} />
            <Route path="/politics/ghmc-2026" element={<GHMCElectionsPage />} />
            <Route path="/politics/ghmc-2026/analysis-prediction" element={<GHMCAnalysisPage />} />
            <Route path="/politics/ghmc-2026/election-dates" element={<GHMCElectionDatesPage />} />
            <Route path="/politics/ghmc-2026/ward/:wardSlug" element={<GHMCWardDetailsPage />} />

            <Route path="/politics" element={<PoliticsHomePage />} />
            <Route path="/politics/:state" element={<PoliticsStatePage />} />
            <Route path="/politics/:state/:constituency" element={<PoliticsConstituencyPage />} />
            <Route path="/politics/:state/:constituency/:candidate" element={<PoliticsCandidatePage />} />

            {/* State Page - Shows list of districts */}
            <Route path="/:state" element={<StatePage />} />

            {/* District page - Shows subdistricts and search */}
            <Route path="/:state/:district" element={<DistrictVillagesPage />} />

            {/* Dynamic route: Could be a Subdistrict (Taluka) or a Village */}
            <Route
              path="/:state/:district/:slug"
              element={<DynamicSlugPage />}
            />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

export default App;
